/**
 * OrbitNote — Texthelp PDF reader / annotation tool
 *
 * Lists its web-accessible resources individually (plus broad scheme globs) matched to
 * every origin with no `use_dynamic_url`. Among them are vendor-named icons under
 * `Chrome/Icons/` and the global content-script bundle under `content-scripts/global/`,
 * all reachable from any page at a fixed URL.
 *
 * The icon is the cleanest signal: `Chrome/Icons/orbitnote-icon-128x128.png` is
 * vendor-named and loads through `probeImage` with no CORS grant, so it survives a
 * restrictive page CSP.
 *
 * Manifest verified against version 7.0.4.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'feepmdlmhplaojabeoecaobfmibooaid';

export const orbitNote: ExtensionDefinition = {
  id: ID,
  name: 'OrbitNote',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 900_000,
  notes:
    'Explicitly lists icons and content scripts as WAR on every origin with no dynamic ' +
    'URL. The vendor-named orbitnote icon is conclusive and needs no CORS grant.',
  signals: [
    {
      id: 'war-orbitnote-icon-128',
      kind: 'web-accessible-resource',
      description:
        'Load Chrome/Icons/orbitnote-icon-128x128.png via <img> — a vendor-named icon ' +
        'exposed to every origin; needs no CORS grant, so it works under a strict CSP.',
      weight: 1,
      run: () => probeImage(ID, 'Chrome/Icons/orbitnote-icon-128x128.png'),
    },
    {
      id: 'war-orbitnote-logo-svg',
      kind: 'web-accessible-resource',
      description:
        'Load Chrome/Icons/orbitnoteLogo.svg via <img> — the vendor logo, a second image ' +
        'path corroborating the identification.',
      weight: 0.5,
      run: () => probeImage(ID, 'Chrome/Icons/orbitnoteLogo.svg'),
    },
    {
      id: 'war-global-content-script',
      kind: 'web-accessible-resource',
      description:
        'Fetch content-scripts/global/contentScript.js — the global injection bundle, ' +
        'an independent non-image path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'content-scripts/global/contentScript.js'),
    },
  ],
};
